import { useMemo, useState } from 'react'

import { BiddingLifecycleAction } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'

import type { ViolationSection } from '~/components/bidding/common/SubmitConfirmModal'
import type { IntradayOrderRow } from '~/hooks/bidding/useListBiddingIntradayOrders'
import { useTransitionBiddingIntradayOrderState } from '~/hooks/bidding/useTransitionBiddingIntradayOrderState'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useNowByMinute } from '~/hooks/useNowByMinute'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { now as serverNow } from '~/models/clock'
import { translateIntradayServerError } from '~/models/intradayServerError'
import { useIntradayApproverContext } from '~/pages/IntradayBidding/hooks/useIntradayApproverContext'
import { intradayApprovalDetailLine } from '~/pages/IntradayBidding/models/intradayApprovalDetailLine'
import {
  buildIntradayInversionWarningSection,
  intradayApprovalInversionDetails,
} from '~/pages/IntradayBidding/models/intradayApprovalInversion'
import { buildIntradayApprovalWarnings } from '~/pages/IntradayBidding/models/intradayApprovalWarnings'
import { intradayRowOperationGateBlockedReason } from '~/pages/IntradayBidding/models/intradayGateClose'
import {
  type IntradayBidLimitViolationDetail,
  buildIntradayBidLimitViolationSection,
  extractIntradayBidLimitViolationDetails,
  intradayViolationSourceBidOf,
} from '~/pages/IntradayBidding/models/intradaySubmitViolation'

export type IntradayApprovalDecision = {
  /** 承認確認 modal の対象 (= undefined で非表示)。 */
  approvingOrder: IntradayOrderRow | undefined
  /** 差戻しダイアログの対象 (= undefined で非表示)。 */
  rejectingOrder: IntradayOrderRow | undefined
  openApprove: (order: IntradayOrderRow) => void
  openReject: (order: IntradayOrderRow) => void
  closeApprove: () => void
  closeReject: () => void
  approve: () => Promise<void>
  reject: (reason: string) => Promise<void>
  isTransitioning: boolean
  /** 承認確認 modal のサマリ (AC-BIN-097: 対象コマ・エリア・売買・量・価格)。 */
  approvalDetailLine: string
  /**
   * 承認確認 modal に出す違反 (AC-BIN-097): テナント入札制限 (直近の APPROVE が
   * `BID_LIMIT_VIOLATION` で拒否された明細)。 JEPX 制度値域は判定しない (backend が作成・
   * 編集の時点で保証し、 承認待ちの札は編集できないので、 承認時に外れた札は現れない)。
   */
  approveViolationSections: ViolationSection[]
  /** 違反があれば承認を確定させない (= 承認確認 modal の確定ボタンを非活性にする)。 */
  hasApproveViolation: boolean
  /**
   * 承認確認 modal の警告 (AC-BIN-098: 承認者非該当 / 自己承認、 AC-BIN-097: 売買逆転)。
   * 承認を止めない注意喚起で、 `SubmitConfirmModal` の `warningSections` にそのまま渡す。
   */
  approvalWarningSections: ViolationSection[]
  /** 差戻しダイアログの説明文 (AC-BIN-098 の警告文を連結済み)。 */
  rejectDescription: string
}

/**
 * 時間前入札の承認 / 差戻し (AC-BIN-095)。 入札一覧の操作列 (AC-BIN-003) と詳細モーダルが
 * 同じ hook を使い、 遷移 (APPROVE/REJECT) ・確定直前の GC 判定 (AC-BIN-099) ・トースト文言・
 * 承認時の違反表示 (AC-BIN-097: APPROVE 拒否から抽出したテナント入札制限違反の明細) ・
 * 確認モーダルの警告をここだけが組み立てる。 確認モーダルと差戻しダイアログの描画は
 * `IntradayApprovalDialogs`。 操作口を押せるか (承認待ちで GC 前) は backend が一覧・詳細の
 * RPC で返す `approvalActionable` で決まり、 ここは確認 UI を開いたまま GC を跨いだ時の
 * 送信を止める。
 *
 * 開く時に渡す札 (`openApprove` / `openReject`) が、 確認モーダルのサマリ・違反抽出に使う
 * 入札値・楽観ロックの基準 (`updatedAt`)・GC 判定の `gcAt`・申請者と今の段の承認者の値の
 * 出所になる (詳細モーダルは詳細 RPC の値を優先した札を渡す)。
 *
 * AC-BIN-098 (承認者判定): 承認者非該当 / 自己承認でも押下は止めず、 確認モーダルに
 * 警告を出す (`approvalWarningSections` / `rejectDescription`)。 売買逆転 (AC-BIN-097) も警告
 * として同じ欄に出す (承認はブロックしない)。 操作口の近傍の常時表示は呼び出し側が持つ。
 */
export const useIntradayApprovalDecision = (options: {
  /**
   * 承認対象と同じ受渡日の注文 (一覧 RPC の行)。 承認確認の売買逆転 (AC-BIN-097) の検知に
   * 使う。 未指定なら売買逆転の警告は出さない。
   */
  orders?: readonly IntradayOrderRow[]
  /** 承認 / 差戻しが成功した後に呼ぶ (詳細モーダルは自身を閉じる)。 */
  onSucceeded?: () => void
  /**
   * 確定直前の GC 判定 (AC-BIN-099) の結果の報告先 (止めなかった時は undefined)。 どこに
   * 出すか (一覧はトースト、 詳細モーダルは下地の Alert) は呼び出し元が決める。
   */
  reportGateCheck: (blockedReason: string | undefined) => void
}): IntradayApprovalDecision => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  const { mutateAsync: transitionAsync, isPending: isTransitioning } =
    useTransitionBiddingIntradayOrderState()
  const approverContext = useIntradayApproverContext()
  const now = useNowByMinute()

  const [approvingOrder, setApprovingOrder] = useState<
    IntradayOrderRow | undefined
  >(undefined)
  const [rejectingOrder, setRejectingOrder] = useState<
    IntradayOrderRow | undefined
  >(undefined)
  /**
   * AC-BIN-097: 直近の承認試行で APPROVE がテナント入札制限違反 (AC-BIN-015、 backend
   * `ensureWithinIntradayBidLimits`) で拒否された明細。 承認確認を開くたび・承認を
   * 試行するたびにクリアする (= 前回試行の結果を持ち越さない)。
   */
  const [bidLimitViolations, setBidLimitViolations] = useState<
    IntradayBidLimitViolationDetail[]
  >([])

  /**
   * 確認 UI は成功 / 送信前中断のときだけ閉じる (`clear`)。 失敗時に閉じると
   * `RejectDialog` が開き直しで reset して差戻し理由が消え、 送信中の loading も見えない。
   */
  const decide = async (
    order: IntradayOrderRow,
    action:
      | typeof BiddingLifecycleAction.APPROVE
      | typeof BiddingLifecycleAction.REJECT,
    reason: string | undefined,
    clear: () => void,
  ): Promise<void> => {
    // 導線の disabled だけでは、 確認 UI を開いたまま GC を跨いだ場合に送信が飛んでしまう
    // (= spot `useSpotApprovalDecision.ensureDecisionAllowed` と同じ二重防御)。
    const blocked = intradayRowOperationGateBlockedReason(
      t,
      order.gcAt,
      serverNow(),
    )
    options.reportGateCheck(blocked)
    if (blocked !== undefined) {
      clear()
      return
    }
    const isApprove = action === BiddingLifecycleAction.APPROVE
    if (isApprove) setBidLimitViolations([])
    try {
      await transitionAsync({
        id: order.id,
        action,
        reason,
        expectedUpdatedAt: order.updatedAt,
      })
    } catch (err) {
      // AC-BIN-097: APPROVE がテナント入札制限違反で拒否されたら、 汎用 toast の代わりに
      // 承認確認 modal 内へ違反明細を出して確定を止める (承認者は札を直せないので
      // 差戻しへ誘導する)。 それ以外の失敗 (楽観ロック競合等) は toast に出す
      // (確認 UI を開いたままにするので、 下地の画面に出しても読めない)。
      const bid = isApprove ? intradayViolationSourceBidOf(order) : undefined
      const violations =
        bid === undefined
          ? []
          : extractIntradayBidLimitViolationDetails(err, bid)
      if (violations.length > 0) {
        setBidLimitViolations(violations)
        return
      }
      showErrorToast({ title: translateIntradayServerError(err, t) })
      return
    }
    showSuccessToast({
      title: isApprove
        ? t('page:biddingIntraday.approvalAction.approveSuccess')
        : t('page:biddingIntraday.approvalAction.rejectSuccess'),
    })
    clear()
    options.onSucceeded?.()
  }

  const orders = options.orders
  const inversionDetails = useMemo(
    () =>
      approvingOrder === undefined || orders === undefined
        ? []
        : intradayApprovalInversionDetails(approvingOrder, orders, now),
    [approvingOrder, orders, now],
  )

  const warningTarget = approvingOrder ?? rejectingOrder
  const { approvalWarningSections, rejectDescription } =
    buildIntradayApprovalWarnings(t, {
      isNotApprover:
        warningTarget !== undefined &&
        approverContext.isNotApprover(warningTarget),
      isSelfApplicant:
        warningTarget !== undefined &&
        approverContext.isSelfApplicant(warningTarget),
      inversionSection:
        approvingOrder === undefined
          ? undefined
          : buildIntradayInversionWarningSection(t, inversionDetails),
      approverListLabel:
        warningTarget === undefined
          ? ''
          : approverContext.approverListLabel(warningTarget),
    })

  const approvalDetailLine =
    approvingOrder === undefined
      ? ''
      : intradayApprovalDetailLine(t, approvingOrder)
  const approveViolationSections: ViolationSection[] = [
    // 提出確認モーダル (T-41) と同じ builder を使い、 解消手順の文言だけ承認者向けに
    // 出し分ける。 承認確認は 1 札の判断で部分送信の概念が無いため sentCount は 0。
    ...(bidLimitViolations.length > 0
      ? [
          buildIntradayBidLimitViolationSection(
            bidLimitViolations,
            0,
            t,
            'approve',
          ),
        ]
      : []),
  ]

  return {
    approvingOrder,
    rejectingOrder,
    openApprove: (order) => {
      setBidLimitViolations([])
      setApprovingOrder(order)
    },
    openReject: setRejectingOrder,
    closeApprove: () => setApprovingOrder(undefined),
    closeReject: () => setRejectingOrder(undefined),
    approve: async () => {
      if (approvingOrder === undefined) return
      await decide(
        approvingOrder,
        BiddingLifecycleAction.APPROVE,
        undefined,
        () => setApprovingOrder(undefined),
      )
    },
    reject: async (reason: string) => {
      if (rejectingOrder === undefined) return
      await decide(rejectingOrder, BiddingLifecycleAction.REJECT, reason, () =>
        setRejectingOrder(undefined),
      )
    },
    isTransitioning,
    approvalDetailLine,
    approveViolationSections,
    hasApproveViolation: bidLimitViolations.length > 0,
    approvalWarningSections,
    rejectDescription,
  }
}
